/**
 * AugmentedDesignBlocks — Sub-Batch 3R (Niranjan, Feb 2026).
 *
 * Renders Federer (1956) Augmented Design specific blocks that the
 * generic per-trait renderer doesn't cover:
 *   • Adjusted Test Means (block-adjusted BLUEs)
 *   • Adjusted Test Details (raw − block effect per plot)
 *   • Block Effects (estimated from checks)
 *   • Critical Differences (4 CDs per Federer §4)
 *   • Standard Errors (4 SEs per Federer §4)
 *   • Dunnett's test vs best check (1955)
 *   • Variance Components (Vg, Ve, Vp, H², GCV, PCV)
 *   • Scalar parameters (MSE, df_error, t_critical, n_superior_to_best_check,
 *     power_rating, dunnett_method)
 *   • Best Check + Best Test Entry highlight banners
 *
 * Used by EnhancedResults.GenericAnalysisView when the trait dict
 * carries augmented-design-specific keys.
 */
import React from 'react';

const fmt = (v, digits = 4) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v);
    return v.toFixed(digits);
  }
  return String(v);
};

const POWER_COLORS = {
  'adequate power': { fill: '#E8F4ED', border: '#16A34A' },
  'marginal power': { fill: '#FFF6E0', border: '#B45309' },
  'low power': { fill: '#FDE7E7', border: '#B11F1F' },
  'F-test essentially uninformative': { fill: '#FDE7E7', border: '#7A1717' },
};

function MiniKV({ title, entries, testId }) {
  if (!entries || !entries.length) return null;
  return (
    <div className="overflow-x-auto mt-3" data-testid={testId}>
      <h5 className="font-semibold text-[#2C2A28] mb-2 text-xs uppercase tracking-wider">{title}</h5>
      <table className="w-full text-xs border-collapse">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-b border-[#E8E6E1]">
              <td className="px-3 py-1.5 font-semibold text-[#2C2A28] bg-[#F4F2EC]" style={{ width: '50%' }}>{k}</td>
              <td className="px-3 py-1.5 text-right font-mono text-[#5E5A56]">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MiniTable({ title, headers, rows, testId, accentColor = '#2D6A4F' }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="overflow-x-auto mt-3" data-testid={testId}>
      <h5 className="font-semibold text-[#2C2A28] mb-2 text-xs uppercase tracking-wider">{title}</h5>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr style={{ background: accentColor }} className="text-white">
            {headers.map(h => (
              <th key={h} className="px-2 py-1.5 text-center font-semibold border border-[#1B4332]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
              {row.map((cell, ci) => {
                // Sub-Batch 3J Ghost-Fix — direction-aware Result badges.
                const cellStr = typeof cell === 'string' ? cell : '';
                const isSuperior = cellStr.includes('Superior');
                const isInferior = cellStr.includes('Inferior');
                const isNs = cellStr === 'ns';
                const legacySig = cellStr === '*' || cellStr === '**';
                return (
                  <td key={ci} className="px-2 py-1 text-center font-mono border border-[#E8E6E1]">
                    {isSuperior ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white">
                        {cell}
                      </span>
                    ) : isInferior ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-rose-600 text-white">
                        {cell}
                      </span>
                    ) : isNs && ci === row.length - 1 ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-stone-200 text-stone-600">
                        ns
                      </span>
                    ) : legacySig ? (
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${cell === '**' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>{cell}</span>
                    ) : cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AugmentedDesignBlocks({ traitData, traitName }) {
  if (!traitData || typeof traitData !== 'object') return null;

  // Detect augmented-specific shape — at least one of these keys must be present
  const isAugmented = (
    traitData.adjusted_test_means
    || traitData.critical_differences
    || traitData.dunnett_results
    || traitData.adjusted_test_details
  );
  if (!isAugmented) return null;

  // ── Best check + best test entry highlight banners ────────────────
  const bestCheck = traitData.best_check;
  const bestTest = traitData.best_test_entry;

  // ── Adjusted Test Means (block-adjusted BLUEs)
  const atmRows = traitData.adjusted_test_means && typeof traitData.adjusted_test_means === 'object'
    ? Object.entries(traitData.adjusted_test_means)
        .sort((a, b) => b[1] - a[1])
        .map(([entry, val], i) => [
          i + 1,
          entry,
          fmt(val, 4),
          bestCheck && entry === bestCheck.name ? '★ Best Check' : '',
        ])
    : [];

  // ── Adjusted Test Details (raw − block_effect per plot)
  const detRows = (traitData.adjusted_test_details || []).map(d => Object.values(d).map(v => typeof v === 'number' ? fmt(v, 4) : v));
  const detHeaders = traitData.adjusted_test_details?.[0] ? Object.keys(traitData.adjusted_test_details[0]) : [];

  // ── Dunnett's test rows — Sub-Batch 3J Ghost-Fix: direction-aware Result
  const dunRows = (traitData.dunnett_results || []).map(d => [
    d.Genotype,
    fmt(d.Adjusted_Mean, 4),
    fmt(d.Diff_vs_Best_Check, 4),
    fmt(d.t_statistic, 4),
    fmt(d.p_value, 4),
    d.Result || 'ns',
  ]);
  const nSup = traitData.n_superior_to_best_check || 0;
  const nInf = traitData.n_inferior_to_best_check || 0;
  const dunCritT = traitData.dunnett_critical_t;

  // ── Critical Differences (4 CDs)
  const cdEntries = traitData.critical_differences
    ? Object.entries(traitData.critical_differences).map(([k, v]) => [k, fmt(v, 4)])
    : [];

  // ── Standard Errors (4 SEs)
  const seEntries = traitData.standard_errors
    ? Object.entries(traitData.standard_errors).map(([k, v]) => [k, fmt(v, 4)])
    : [];

  // ── Block Effects
  const blkEntries = traitData.block_effects
    ? Object.entries(traitData.block_effects).map(([k, v]) => [`Block ${k}`, fmt(v, 4)])
    : [];

  // ── Check means
  const chkEntries = traitData.check_means
    ? Object.entries(traitData.check_means).map(([k, v]) => [k, fmt(v, 4)])
    : [];

  // ── Variance components — Sub-Batch 3J Ghost-Fix: proper acronym labels
  const VC_LABEL_MAP = {
    'Vg_raw_signed': 'Vg (raw, signed)',
    'Vg': 'Vg (Genotypic Variance)',
    'Ve': 'Ve (Environmental Variance)',
    'Vp': 'Vp (Phenotypic Variance)',
    'H2_broad_sense': 'H\u00b2 (broad sense)',
    'GCV (%)': 'GCV (%)',
    'PCV (%)': 'PCV (%)',
  };
  const vcEntries = traitData.variance_components
    ? Object.entries(traitData.variance_components).map(([k, v]) => [
        VC_LABEL_MAP[k] || k.replace(/_/g, ' '),
        typeof v === 'number' ? fmt(v, 4) : v,
      ])
    : [];

  // ── Scalar audit panel
  const scalarEntries = [];
  if (typeof traitData.grand_mean === 'number') scalarEntries.push(['Grand mean', fmt(traitData.grand_mean, 4)]);
  if (typeof traitData.overall_check_mean === 'number') scalarEntries.push(['Overall check mean', fmt(traitData.overall_check_mean, 4)]);
  if (typeof traitData.mse === 'number') scalarEntries.push(['MSE', fmt(traitData.mse, 4)]);
  if (typeof traitData.sqrt_mse === 'number') scalarEntries.push(['√MSE', fmt(traitData.sqrt_mse, 4)]);
  if (typeof traitData.df_error === 'number') scalarEntries.push(['df_error', String(traitData.df_error)]);
  if (typeof traitData.cv_percent === 'number') scalarEntries.push(['CV (%)', `${fmt(traitData.cv_percent, 2)}%`]);
  if (typeof traitData.t_critical === 'number') scalarEntries.push(['t-critical (5%)', fmt(traitData.t_critical, 4)]);
  if (typeof traitData.n_superior_to_best_check === 'number') scalarEntries.push(['# test entries \u25b6 superior to best check', String(traitData.n_superior_to_best_check)]);
  if (typeof traitData.n_inferior_to_best_check === 'number') scalarEntries.push(['# test entries \u25c0 inferior to best check', String(traitData.n_inferior_to_best_check)]);
  if (typeof traitData.dunnett_critical_t === 'number') scalarEntries.push(['Dunnett critical |t| (\u03b1=0.05)', fmt(traitData.dunnett_critical_t, 4)]);
  if (traitData.dunnett_method) scalarEntries.push(['Dunnett test method', traitData.dunnett_method]);

  // ── Power rating banner
  const pr = traitData.power_rating;
  const dfE = traitData.df_error;
  const powerBanner = pr && (POWER_COLORS[pr] || { fill: '#FFF6E0', border: '#B45309' });

  return (
    <div className="mt-4 rounded-xl border border-[#D5D1CC] bg-[#FBFAF7] p-4" data-testid={`augmented-blocks-${traitName}`}>
      <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">
        Augmented Design — Federer (1956) Output
      </h4>

      {/* ── Best Check + Best Test Entry highlight banners ───────────── */}
      {(bestCheck || bestTest) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          {bestCheck && (
            <div className="rounded-md bg-[#E8F4ED] border border-[#16A34A] p-3" data-testid={`best-check-${traitName}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#16A34A] font-semibold">Best Check</div>
              <div className="text-base font-bold text-[#1B4332] mt-1">{bestCheck.name}</div>
              <div className="text-xs font-mono text-[#2C2A28]">{fmt(bestCheck.value, 4)}</div>
            </div>
          )}
          {bestTest && (
            <div className="rounded-md bg-[#EAF0EC] border border-[#2D6A4F] p-3" data-testid={`best-test-${traitName}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#2D6A4F] font-semibold">Best Test Entry (BLUE-based)</div>
              <div className="text-base font-bold text-[#1B4332] mt-1">{bestTest.name}</div>
              <div className="text-xs font-mono text-[#2C2A28]">{fmt(bestTest.value, 4)}</div>
            </div>
          )}
        </div>
      )}

      {/* ── Power rating banner ───────────────────────────────────────── */}
      {pr && powerBanner && (
        <div
          className="rounded-md p-2 my-3 text-xs"
          style={{ background: powerBanner.fill, border: `1.5px solid ${powerBanner.border}` }}
          data-testid={`power-rating-${traitName}`}
        >
          <strong>Statistical Power:</strong> {pr}
          {dfE != null && (
            <span className="ml-1">(df<sub>error</sub> = {dfE} → {dfE >= 12 ? 'adequate power for typical comparisons' : dfE >= 6 ? 'marginal power; interpret p-values cautiously' : dfE >= 2 ? 'low power; only large effects (>2 SD) will be detected' : 'F-test essentially uninformative'})</span>
          )}
        </div>
      )}

      {/* ── Scalar audit panel ───────────────────────────────────────── */}
      {scalarEntries.length > 0 && (
        <MiniKV title="ANOVA Audit & Scalars" entries={scalarEntries} testId={`aug-scalars-${traitName}`} />
      )}

      {/* ── Adjusted Test Means (BLUE) — sorted descending ────────────── */}
      {atmRows.length > 0 && (
        <>
          <MiniTable
            title="Adjusted Test Means (Federer BLUEs) — sorted by performance"
            headers={['Rank', 'Genotype', 'Adjusted Mean', 'Note']}
            rows={atmRows}
            testId={`aug-adj-means-${traitName}`}
          />
          <div
            className="mt-2 px-3 py-2 rounded-md bg-[#F4F2EC] border border-[#D5D1CC] text-[11px] text-[#5E5A56] italic"
            data-testid={`aug-cld-footnote-${traitName}`}
          >
            <strong>&#9432; CLD vs Dunnett — which to use:</strong>{' '}
            CLD answers &ldquo;which test entries differ significantly from{' '}
            <em>each other</em>?&rdquo; Test entries sharing a letter are
            statistically indistinguishable. For comparison against the
            best check (with direction-aware verdicts), see Dunnett&rsquo;s
            test table below.
          </div>
        </>
      )}

      {/* ── Adjusted Test Details (per-plot derivation) ──────────────── */}
      {detRows.length > 0 && (
        <MiniTable
          title="Per-Plot BLUE Derivation (raw − block effect)"
          headers={detHeaders}
          rows={detRows}
          testId={`aug-det-${traitName}`}
        />
      )}

      {/* ── Dunnett's test vs best check — Sub-Batch 3J Ghost-Fix ─────── */}
      {dunRows.length > 0 && (
        <>
          <MiniTable
            title={`Dunnett's Test (1955, two-sided) — Each Test Entry vs Best Check${bestCheck ? ` (${bestCheck.name} = ${fmt(bestCheck.value, 2)})` : ''}`}
            headers={['Genotype', 'Adjusted Mean', '\u0394 vs Best Check', 't-statistic', 'p-value (adj.)', 'Result']}
            rows={dunRows}
            testId={`aug-dunnett-${traitName}`}
          />
          <div
            className="mt-2 px-3 py-2 rounded-md bg-[#F4F2EC] border border-[#D5D1CC] text-[11px] text-[#5E5A56] italic"
            data-testid={`aug-dunnett-footnote-${traitName}`}
          >
            {traitData.dunnett_method && (
              <div className="mb-1">
                <strong>Method:</strong> {traitData.dunnett_method}
                {dunCritT != null && (
                  <span> &nbsp;&middot;&nbsp; Critical |t| (Dunnett) = <strong>{fmt(dunCritT, 4)}</strong></span>
                )}
              </div>
            )}
            <strong>&#9432; Dunnett vs CLD — which to use:</strong>{' '}
            Dunnett&rsquo;s test answers &ldquo;which test entries differ
            significantly from the <em>best check</em>?&rdquo; The
            <strong> Result</strong> column is direction-aware:{' '}
            <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold">▶ Superior</span>{' '}
            means adjusted mean &gt; best check AND p&lt;0.05;{' '}
            <span className="inline-block px-1.5 py-0.5 rounded bg-rose-600 text-white text-[10px] font-bold">◀ Inferior</span>{' '}
            means adjusted mean &lt; best check AND p&lt;0.05 (worse than
            check, <em>not</em> a selection candidate). On this trait:{' '}
            <strong>{nSup}</strong> Superior, <strong>{nInf}</strong> Inferior,{' '}
            <strong>{dunRows.length - nSup - nInf}</strong> ns. For
            test-vs-test comparisons see the CLD on the Adjusted Test Means table.
          </div>
        </>
      )}

      {/* ── 4 Critical Differences (Federer §4) ──────────────────────── */}
      {cdEntries.length > 0 && (
        <MiniKV title="Critical Differences (Federer 1956 §4)" entries={cdEntries} testId={`aug-cd-${traitName}`} />
      )}

      {/* ── 4 Standard Errors (Federer §4) ───────────────────────────── */}
      {seEntries.length > 0 && (
        <MiniKV title="Standard Errors (Federer 1956 §4)" entries={seEntries} testId={`aug-se-${traitName}`} />
      )}

      {/* ── Block Effects (from check-only ANOVA) ────────────────────── */}
      {blkEntries.length > 0 && (
        <MiniKV title="Block Effects (estimated from checks)" entries={blkEntries} testId={`aug-blk-${traitName}`} />
      )}

      {/* ── Check means ──────────────────────────────────────────────── */}
      {chkEntries.length > 0 && (
        <MiniKV title="Check Means" entries={chkEntries} testId={`aug-chk-${traitName}`} />
      )}

      {/* ── Variance components (BLUE-based, Sub-Batch 3G) ───────────── */}
      {vcEntries.length > 0 && (
        <MiniKV title="Variance Components (BLUE-based, Hallauer-Carena 2010)" entries={vcEntries} testId={`aug-vc-${traitName}`} />
      )}
    </div>
  );
}

export default AugmentedDesignBlocks;
